import Box from "@mui/material/Box/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import { useTokens } from "api/accessTokens";
import { EmptyState } from "components/EmptyState/EmptyState";
import { FormSection } from "components/FormControls/FormSection";
import { TextInput } from "components/FormControls/TextInput";
import { ReactComponent as Copy02Icon } from "components/Icons/copy-02.svg";
import { ReactComponent as Edit03Icon } from "components/Icons/edit-03.svg";
import { ReactComponent as PlusIcon } from "components/Icons/plus.svg";
import { ReactComponent as KeyIcon } from "components/Icons/Security-Key-01.svg";
import { ReactComponent as XCloseIcon } from "components/Icons/x-close.svg";
import { ConfirmationModal } from "components/Tooltips/ConfirmationModal";
import { OLModal } from "components/Tooltips/OLModal";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useNotifications } from "contexts/Toast/ToastProvider";
import dayjs from "dayjs";
import { useMessage } from "hooks/intl";
import { OLButton } from "OLButton";
import { Fragment, useState } from "react";
import { useParams } from "react-router-dom";
import { Token } from "types/general";
import { useStyles } from "./AdminForm.styles";
import { ManageAccessTokenModal } from "./ManageAccessTokenModal";

enum Modals {
  GenerateToken,
  CopyToken,
  DeleteToken,
  EditToken,
}

export function AccessToken({
  tokensList,
  editMode,
}: {
  tokensList?: Token[];
  editMode: boolean;
}) {
  const classes = useStyles();
  const { notify } = useNotifications();
  const { message } = useMessage();
  const { deleteToken } = useTokens();
  const { id: adminId } = useParams<{ id: string }>();

  const selectedModalInitialState = { token: "", mode: null, isOpen: false };

  const [token, setToken] = useState("");
  const [selectedModal, setSelectedModal] = useState<{
    token?: string;
    mode: Modals | null;
    isOpen: boolean;
  }>(selectedModalInitialState);
  const [isLoading, setIsLoading] = useState(false);

  const maxTokensNumber = 5;
  const maxNumberOfTokensAdded =
    tokensList && tokensList.length >= maxTokensNumber;
  const isAnyTokenAdded = tokensList && tokensList.length > 0;

  const editedToken =
    selectedModal.mode === Modals.EditToken
      ? tokensList?.find((token) => token.apiKeyId === selectedModal.token)
      : undefined;

  const removeToken = async () => {
    if (typeof adminId === "string" && !!selectedModal.token) {
      setIsLoading(true);
      const isTokenDeleted = await deleteToken(adminId, selectedModal.token);

      if (isTokenDeleted) {
        setIsLoading(false);
        setSelectedModal(selectedModalInitialState);
        notify(message({ id: "apiKeySuccessfullyDeleted" }));
      }
    }
  };

  const getTokenInfo = (token: Token) => {
    const isTokenExpired = dayjs(token.expirationDate).isBefore(dayjs());
    const formatDate = (date: string) =>
      dayjs(date).format("D MMM YYYY, hh:mm A");

    if (isTokenExpired) {
      return (
        <span className={classes.expired}>
          {message({ id: "expiresOn" })}: {message({ id: "expired" })}
        </span>
      );
    } else {
      return `${message({ id: "expiresOn" })}:
      ${
        token.expirationDate
          ? formatDate(token.expirationDate)
          : message({ id: "neverExpires" })
      }, ${message({ id: "lastUse" })}: 
      ${token.lastUsageAt ? formatDate(token.lastUsageAt) : "-"}`;
    }
  };

  return (
    <>
      <FormSection
        isLoading={editMode && typeof tokensList === "undefined"}
        title={message({ id: "permanentUserToken" })}
      >
        {!tokensList || tokensList?.length === 0 ? (
          <EmptyState
            icon={<KeyIcon />}
            title={message({ id: "accessToken" })}
            description={message({
              id: "generateNewKeyForAuthentication",
            })}
            className={classes.emptyState}
          />
        ) : (
          isAnyTokenAdded && (
            <Grid container>
              {tokensList?.map((token, index) => {
                return (
                  <Fragment key={token.apiKeyId}>
                    <Grid item xs={11} pr={8}>
                      <TextInput
                        name={token.name}
                        key={token.apiKeyId}
                        width="100%"
                        data-test-id={`token-${index}`}
                        label={token.name}
                        value={token.token}
                        InputProps={{
                          readOnly: true,
                        }}
                        className={classes.tokenInput}
                        disabled
                      />
                    </Grid>
                    <Grid item xs={1} alignItems="center" display="flex">
                      <div className={classes.iconBox}>
                        <Edit03Icon
                          onClick={() =>
                            setSelectedModal({
                              token: token.apiKeyId,
                              mode: Modals.EditToken,
                              isOpen: true,
                            })
                          }
                          className={classes.icon}
                          data-test-id={`token-${index}-edit`}
                        />
                      </div>
                      <div className={classes.iconBox}>
                        <XCloseIcon
                          onClick={() =>
                            setSelectedModal({
                              token: token.apiKeyId,
                              mode: Modals.DeleteToken,
                              isOpen: true,
                            })
                          }
                          className={classes.icon}
                          data-test-id={`token-${index}-remove`}
                        />
                      </div>
                    </Grid>
                    <Grid item xs={10} mb={12}>
                      <Typography className={classes.tokenInfo}>
                        {getTokenInfo(token)}
                      </Typography>
                    </Grid>
                  </Fragment>
                );
              })}
            </Grid>
          )
        )}
        <Box
          display="flex"
          justifyContent={isAnyTokenAdded ? "left" : "center"}
          mb={isAnyTokenAdded ? 0 : 12}
        >
          <Tooltip
            placement="right"
            text={
              <>
                <Typography className={classes.tooltipTitle}>
                  {message({
                    id: "generateNewKey",
                  })}
                </Typography>
                <Typography className={classes.tooltipText}>
                  {message({
                    id: "generateApiKeyTooltip",
                  })}
                </Typography>
              </>
            }
            disableHoverListener={editMode || maxNumberOfTokensAdded}
          >
            <span>
              <OLButton
                color="primary"
                variant="contained"
                data-test-id="generate-token"
                size="small"
                startIcon={<PlusIcon />}
                disabled={!editMode || maxNumberOfTokensAdded}
                onClick={() =>
                  setSelectedModal({
                    mode: Modals.GenerateToken,
                    isOpen: true,
                  })
                }
              >
                {message({ id: "generateNewKey" })}
              </OLButton>
            </span>
          </Tooltip>
        </Box>
        {selectedModal.mode === Modals.GenerateToken && (
          <ManageAccessTokenModal
            title="generateApiKey"
            submitText="generateKey"
            open={selectedModal.isOpen}
            openTokenModal={(token: string) => {
              setToken(token);
              setSelectedModal({ mode: Modals.CopyToken, isOpen: true });
            }}
            close={() => setSelectedModal(selectedModalInitialState)}
          />
        )}
        {selectedModal.mode === Modals.EditToken && (
          <ManageAccessTokenModal
            title="editApiKey"
            submitText="saveChanges"
            open={selectedModal.isOpen}
            close={() => setSelectedModal(selectedModalInitialState)}
            initialValues={editedToken}
          />
        )}
        {selectedModal.mode === Modals.CopyToken && (
          <OLModal
            className={classes.modal}
            open={selectedModal.isOpen}
            submitText={message({ id: "close" })}
            header={message({ id: "generateApiKey" })}
            submitAction={() => {
              setSelectedModal(selectedModalInitialState);
              setToken("");
            }}
            onClose={() => {
              setSelectedModal(selectedModalInitialState);
              setToken("");
            }}
            data-test-id="api-key-modal"
          >
            <>
              <Typography className={classes.copyTokenInfo}>
                {message({
                  id: "copyApiKeyInfo",
                })}
              </Typography>
              <TextInput
                name="apiKey"
                width="100%"
                data-test-id="copy-token-input"
                label={message({ id: "apiKey" })}
                value={token}
                className={classes.copyTokenInput}
                InputProps={{
                  endAdornment: (
                    <Copy02Icon
                      className={classes.copyIcon}
                      data-test-id="copy-token-icon"
                      onClick={() => {
                        if (typeof token === "string") {
                          navigator.clipboard.writeText(token).then(() => {
                            notify(
                              `${message({ id: "apiKey" })} ${message({ id: "copied" })}`,
                            );
                          });
                        }
                      }}
                    />
                  ),
                  readOnly: true,
                }}
              />
            </>
          </OLModal>
        )}
        {selectedModal.mode === Modals.DeleteToken && (
          <ConfirmationModal
            modalWidth={536}
            open={selectedModal.isOpen}
            header="deletedApiKeyConfirmation"
            info="youCantUndoThisAction"
            submitText="yesDelete"
            cancelText="cancel"
            data-test-id="delete-api-key"
            isSubmitDisabled={isLoading}
            action={removeToken}
            close={() => setSelectedModal(selectedModalInitialState)}
          />
        )}
      </FormSection>
    </>
  );
}
